import React, { useState, useEffect, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBook, faBookOpen, faPlayCircle } from '@fortawesome/free-solid-svg-icons';
import {
  domParser,
  extractTextAndUrls,
} from "../../../core/common/ults/domParser";
import { useTranslation } from 'react-i18next';
import { useTrackVideo } from "../../../core/hooks/useTrackVideo";
import { useAuth } from "../../../core/hooks/useAuth";
import { Modal, Spin } from "antd";

const CourseWatch = () => {
  const { t } = useTranslation();
  const courseLesson = useSelector(
    (state: any) => state.courseLessonSlice.courseLesson
  );

  const { mutate: trackVideo } = useTrackVideo()
  const { moodleUser } = useAuth()
  const { id } = useParams();

  const { courseData, selectedLesson } = courseLesson;
  const [videoUrl, setVideoUrl] = useState("");
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [fileType, setFileType] = useState<"video" | "pdf" | "doc" | "audio" | "unknown">("unknown");
  const [selectedItems, setSelectedItems] = useState<{
    sectionIndex: number;
    lessonIndex: number;
    itemIndex: number;
  }>({
    sectionIndex: Number(selectedLesson?.sectionIndex ?? 0),
    lessonIndex: Number(selectedLesson?.lessonIndex ?? 0),
    itemIndex: Number(selectedLesson?.itemIndex ?? 0),
  });
  const [isSavingProgress, setIsSavingProgress] = useState(false);
  const isSeekingRef = useRef(false);

  const getActiveMedia = (): HTMLMediaElement | null => {
    if (fileType === "audio") return audioRef.current;
    if (fileType === "video") return videoRef.current;
    return null;
  };

  const isYouTubeUrl = (url: string) => {
    if (!url) return false;
    return url.includes("youtube.com") || url.includes("youtu.be");
  };

  const getFileType = (url: string) => {
    if (!url) return "unknown";
    const lowerUrl = url.toLowerCase().split("?")[0];
    if (lowerUrl.endsWith('.pdf')) return "pdf";
    if (lowerUrl.endsWith('.doc') || lowerUrl.endsWith('.docx')) return "doc";
    if (
      lowerUrl.endsWith('.mp3') ||
      lowerUrl.endsWith('.wav') ||
      lowerUrl.endsWith('.m4a') ||
      lowerUrl.endsWith('.aac')
    ) return "audio";
    if (isYouTubeUrl(url) || lowerUrl.endsWith('.mp4') || lowerUrl.endsWith('.webm') || lowerUrl.endsWith('.ogg')) return "video";
    return "unknown";
  };

  const isSelectedItem = (
    sectionIndex: number,
    lessonIndex: number,
    itemIndex: number
  ) =>
    Number(selectedItems.sectionIndex) === Number(sectionIndex) &&
    Number(selectedItems.lessonIndex) === Number(lessonIndex) &&
    Number(selectedItems.itemIndex) === Number(itemIndex);

  const trackedPdfUrlRef = useRef<string | null>(null);

  const sendPdfTracking = (pdfUrl: string) => {
    if (!moodleUser || !pdfUrl || !id) return;
    if (trackedPdfUrlRef.current === pdfUrl) return;

    trackedPdfUrlRef.current = pdfUrl;
    trackVideo({
      userid: moodleUser.id,
      courseid: id,
      filetype: "pdf",
      pdfid: pdfUrl,
      isseen: 1,
    });
  };

  const handleItemClick = (
    sectionIndex: number,
    lessonIndex: number,
    itemIndex: number
  ) => {
    setSelectedItems({
      sectionIndex: Number(sectionIndex),
      lessonIndex: Number(lessonIndex),
      itemIndex: Number(itemIndex),
    });
  };

  const handleVideoClick = (
    url: string,
    sectionIndex: number,
    lessonIndex: number,
    itemIndex: number
  ) => {
    handleItemClick(sectionIndex, lessonIndex, itemIndex);
    const nextFileType = getFileType(url);
    setVideoUrl(url);
    setFileType(nextFileType);

    // PDF: gọi track ngay khi chọn item
    if (nextFileType === "pdf") {
      sendPdfTracking(url);
    }
  };

  // Thêm useEffect để cập nhật videoUrl khi selectedItems thay đổi
  useEffect(() => {
    const currentSection =
      courseData?.courseContentData?.[selectedItems.sectionIndex + 1];
    const unilabelModule =
      currentSection?.modules?.[Number(selectedItems.lessonIndex)];

    if (unilabelModule?.modname === "unilabel") {
      const { items } = extractTextAndUrls(unilabelModule.description);
      const selectedMaterial = items?.[Number(selectedItems.itemIndex)];
      if (selectedMaterial?.url) {
        const nextFileType = getFileType(selectedMaterial.url);
        setVideoUrl(selectedMaterial.url);
        setFileType(nextFileType);

        if (nextFileType === "pdf") {
          sendPdfTracking(selectedMaterial.url);
        }
      }
    }
  }, [selectedItems, courseData, moodleUser, id]);
  const getYouTubeEmbedUrl = (url: string) => {
    if (url.includes("youtube.com/watch")) {
      const videoId = url.split("v=")[1].split("&")[0];
      return `https://www.youtube.com/embed/${videoId}`;
    } else if (url.includes("youtu.be")) {
      const videoId = url.split("youtu.be/")[1];
      return `https://www.youtube.com/embed/${videoId}`;
    }
    return url;
  };

  useEffect(() => {
    if (selectedLesson) {
      // Tự động mở section chứa bài học được chọn
      const sectionElement = document.getElementById(
        `SpacingOne${selectedLesson.sectionIndex}`
      );
      if (sectionElement) {
        sectionElement.classList.add("show");
      }
      // Tự động chọn video của bài học
      if (selectedLesson.lesson?.url) {
        handleVideoClick(
          selectedLesson.lesson.url,
          Number(selectedLesson.sectionIndex),
          Number(selectedLesson.lessonIndex),
          Number(selectedLesson.itemIndex)
        );
      }
    }
  }, [selectedLesson]);

  const hasTrackedPlayRef = useRef(false);
  const pauseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const sendTracking = async (eventType = "play") => {
    const media = getActiveMedia();
    if (!media || !moodleUser || !videoUrl) return;
    if (fileType !== "video" && fileType !== "audio") return;

    const playtime = Math.floor(media.currentTime);

    return new Promise<void>((resolve) => {
      trackVideo({
        userid: moodleUser.id,
        videoid: videoUrl,
        eventtype: eventType,
        playtime: playtime,
        courseid: id!,
        filetype: fileType,
      });
      resolve();
    });
  };

  const handlePlay = () => {
    if (pauseTimeoutRef.current) {
      clearTimeout(pauseTimeoutRef.current);
      pauseTimeoutRef.current = null;
    }
    setIsSavingProgress(false);

    // Chỉ gọi API 1 lần mỗi lần bắt đầu play (không gọi liên tục khi đang phát)
    if (hasTrackedPlayRef.current) return;
    hasTrackedPlayRef.current = true;
    sendTracking("play");
  };

  const handlePause = async () => {
    if (pauseTimeoutRef.current) {
      clearTimeout(pauseTimeoutRef.current);
      pauseTimeoutRef.current = null;
    }
    pauseTimeoutRef.current = setTimeout(async () => {
      const media = getActiveMedia();
      if (isSeekingRef.current || media?.seeking) {
        return;
      }
      hasTrackedPlayRef.current = false;
      setIsSavingProgress(true);
      await sendTracking("stop");
      setIsSavingProgress(false);
    }, 250);
  };

  const handleSeeking = () => {
    isSeekingRef.current = true;
    if (pauseTimeoutRef.current) {
      clearTimeout(pauseTimeoutRef.current);
      pauseTimeoutRef.current = null;
    }
  };

  const handleSeeked = () => {
    isSeekingRef.current = false;
    // Do not show modal; optionally record a lightweight event
    sendTracking("seeked");
  };

  const handleEnded = async () => {
    if (pauseTimeoutRef.current) {
      clearTimeout(pauseTimeoutRef.current);
      pauseTimeoutRef.current = null;
    }
    hasTrackedPlayRef.current = false;
    setIsSavingProgress(true);
    await sendTracking("ended");
    setIsSavingProgress(false);
  };

  useEffect(() => {
    if (fileType !== "video" && fileType !== "audio") return;

    const media = getActiveMedia();
    if (!media) return;

    hasTrackedPlayRef.current = false;

    media.addEventListener("play", handlePlay);
    media.addEventListener("pause", handlePause);
    media.addEventListener("ended", handleEnded);
    media.addEventListener("seeking", handleSeeking);
    media.addEventListener("seeked", handleSeeked);

    return () => {
      media.removeEventListener("play", handlePlay);
      media.removeEventListener("pause", handlePause);
      media.removeEventListener("ended", handleEnded);
      media.removeEventListener("seeking", handleSeeking);
      media.removeEventListener("seeked", handleSeeked);
      if (pauseTimeoutRef.current) {
        clearTimeout(pauseTimeoutRef.current);
        pauseTimeoutRef.current = null;
      }
    };
  }, [videoUrl, fileType]);

  useEffect(() => {
    const handleUnload = () => sendTracking("unload");
    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, []);

  useEffect(() => {
    const handleOffline = () => {
      const media = getActiveMedia();
      if (media && !media.paused) {
        media.pause();
      }
    };

    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("offline", handleOffline);
    };
  }, [fileType]);

  return (
    <>
      <>
        {/* Course watch */}
        <div className="content pt-0">
          <div className="container-fluid">
            <div className="course-watch-section">
              <div className="row">
                <div className="col-lg-4 border-end">
                  <div className="progress-overview-section">
                    <div className="mb-4">
                      <Link
                        to={`${all_routes.courseDetails}/${courseData?.courseDetail?.id}`}
                        className="back-to-course"
                      >
                        <i className="isax isax-arrow-left me-1" />
                        {t('courseWatch.backToCourse')}
                      </Link>
                    </div>
                    <h3>{courseData?.courseDetail?.fullname}</h3>
                    <div
                      className="accordions-items-seperate"
                      id="accordionSpacingExample"
                    >
                      {courseData?.courseContentData
                        ?.slice(1)
                        ?.map((section: any, sectionIndex: any) => {
                          return (
                            <div className="accordion-item" key={sectionIndex}>
                              <div
                                className="accordion-header"
                                id={`headingSpacingOne${sectionIndex}`}
                              >
                                <div
                                  className="accordion-button collapsed"
                                  role="button"
                                  data-bs-toggle="collapse"
                                  data-bs-target={`#SpacingOne${sectionIndex}`}
                                  aria-expanded="false"
                                  aria-controls={`SpacingOne${sectionIndex}`}
                                >
                                  <div>
                                    <h6 className="mb-0">{section?.name}</h6>
                                  </div>
                                </div>
                              </div>
                              <div
                                id={`SpacingOne${sectionIndex}`}
                                className="accordion-collapse collapse"
                                aria-labelledby={`headingSpacingOne${sectionIndex}`}
                                data-bs-parent="#accordionSpacingExample"
                              >
                                <div className="accordion-body">
                                  {section?.modules?.map(
                                    (item: any, lessonIndex: number) => {
                                      if (item?.modname === "unilabel") {
                                        const { title, items } =
                                          extractTextAndUrls(item?.description);
                                        return (
                                          <div
                                            key={item.id || lessonIndex}
                                            className="unilabel-group mb-3"
                                          >
                                            <h6 className="mb-2 fw-semibold">
                                              {title ||
                                                t(
                                                  "courseDetails.learningMaterials"
                                                )}
                                            </h6>
                                            {items?.map(
                                              (lesson: any, itemIndex: number) => (
                                                <div
                                                  className={`lesson-item d-flex align-items-center justify-content-between mb-3 p-3 ${
                                                    isSelectedItem(
                                                      sectionIndex,
                                                      lessonIndex,
                                                      itemIndex
                                                    )
                                                      ? "active-lesson"
                                                      : ""
                                                  }`}
                                                  key={`${lessonIndex}-${itemIndex}`}
                                                  onClick={() =>
                                                    handleVideoClick(
                                                      lesson?.url,
                                                      sectionIndex,
                                                      lessonIndex,
                                                      itemIndex
                                                    )
                                                  }
                                                >
                                                  <div className="d-flex align-items-center">
                                                    <span className="d-flex">
                                                      {["video", "audio"].includes(
                                                        getFileType(lesson?.url)
                                                      ) ? (
                                                        <i className="isax isax-play-circle5 text-success fs-24 me-1" />
                                                      ) : (
                                                        <FontAwesomeIcon
                                                          icon={faBookOpen}
                                                          className="text-success fs-24  me-1"
                                                          size="xl"
                                                        />
                                                      )}
                                                    </span>
                                                    <p className="accordian-content mb-0">
                                                      {lesson?.text}
                                                    </p>
                                                  </div>
                                                </div>
                                              )
                                            )}
                                          </div>
                                        );
                                      }
                                    }
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>
                <div className="col-lg-8">
                  <div className="course-watch-content">
                    {fileType === "audio" ? (
                      <div className="position-relative video-container">
                        {!videoUrl ? (
                          <div className="no-video-message">
                            {t('courseWatch.pleaseSelectLesson')}
                          </div>
                        ) : (
                          <audio
                            ref={audioRef}
                            className="w-100"
                            controls
                            preload="auto"
                            src={videoUrl}
                            style={{ borderRadius: "8px" }}
                            key={videoUrl}
                          />
                        )}
                      </div>
                    ) : fileType === "video" ? (
                      <div className="position-relative video-container">
                        {!videoUrl ? (
                          <div className="no-video-message">
                            {t('courseWatch.pleaseSelectLesson')}
                          </div>
                        ) : isYouTubeUrl(videoUrl) ? (
                          <iframe
                            width="100%"
                            height="500"
                            src={getYouTubeEmbedUrl(videoUrl)}
                            title="YouTube video player"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            style={{ borderRadius: "8px" }}
                          />
                        ) : (
                          <video
                            ref={videoRef}
                            className="video-player"
                            controls
                            preload="auto"
                            width="100%"
                            height="500"
                            style={{ borderRadius: "8px" }}
                            key={videoUrl}
                          >
                            <source src={videoUrl} type="video/mp4" />
                            <source src={videoUrl} type="video/webm" />
                            <source src={videoUrl} type="video/ogg" />
                            {t('courseWatch.videoNotSupported')}
                          </video>
                        )}

                      </div>
                    ) : (
                      <div className="position-relative document-container" style={{ height: "calc(100vh - 200px)" }}>
                        {!videoUrl ? (
                          <div className="no-video-message">
                            {t('courseWatch.pleaseSelectLesson')}
                          </div>
                        ) : fileType === "pdf" ? (
                          <iframe
                            src={`${videoUrl}#toolbar=0`}
                            width="100%"
                            style={{ borderRadius: "8px", border: "none", height: "100%" }}
                            title="PDF Viewer"
                          />
                        ) : fileType === "doc" ? (
                          <div className="document-content" style={{
                            padding: "20px",
                            textAlign: "center",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center"
                          }}>
                            <h4>{t('courseWatch.wordDocument')}</h4>
                            <p>{t('courseWatch.pleaseDownloadToView')}</p>
                            <a
                              href={videoUrl}
                              className="btn btn-primary"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {t('courseWatch.downloadDocument')}
                            </a>
                          </div>
                        ) : (
                          <div className="no-video-message">
                            {t('courseWatch.unsupportedFileFormat')}
                          </div>
                        )}
                      </div>
                    )}
                    <ul
                      className="nav-tabs mb-4 border-0 nav-style-1 d-sm-flex d-block pt-3"
                      role="tablist"
                    >
                      <li className="nav-item active">
                        <Link
                          className="btn nav-link active"
                          data-bs-toggle="tab"
                          role="tab"
                          to="#overview"
                          aria-selected="false"
                        >
                          {t('courseWatch.overview')}
                        </Link>
                      </li>
                      <li className="nav-item">
                        <Link
                          className="btn nav-link"
                          data-bs-toggle="tab"
                          role="tab"
                          to="#notes"
                          aria-selected="false"
                        >
                          {t('courseWatch.notes')}
                        </Link>
                      </li>
                    </ul>
                    <div className="tab-content">
                      <div
                        className="tab-pane active show"
                        id="overview"
                        role="tabpanel"
                      >
                        <div className="mb-4">
                          <h6 className="fs-18 fw-semibold mb-1">
                            {t('courseWatch.aboutThisCourse')}
                          </h6>
                          <p
                            className="fs-14 mb-3"
                            dangerouslySetInnerHTML={{
                              __html: domParser(
                                courseData?.courseDetail?.summary || ""
                              ),
                            }}
                          />
                        </div>
                      </div>
                      <div
                        className="tab-pane"
                        id="notes"
                        role="tabpanel"
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* /Course watch */}


        {/* modal loading */}
        <Modal open={isSavingProgress} footer={null} closable={false}>
          <div style={{ textAlign: "center" }}>
            <Spin size="large" />
            <p style={{ marginTop: 16 }}>Đang lưu tiến trình...</p>
          </div>
        </Modal>
      </>
    </>
  );
};

export default CourseWatch;
